import { useEffect, useState } from "react"
import { faBan, faCheckCircle, faDollarSign, faFile, faUser } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserTieHair } from "@fortawesome/pro-solid-svg-icons"
import numeral from 'numeral'
import InputMask from "react-input-mask"

import ButtonPrimary from "components/ButtonPrimary"

import { Form, Input, Select } from 'antd'

import states from 'lib/states'

import styles from "./styles.module.scss"

const PowerForm = ({ className, formClassName, contract, employees, newPower, onSave, onCancel }) => {
  const [addedValues, setAddedValues] = useState({})

  const [form] = Form.useForm();

  useEffect(() => {
    if (newPower && Object.keys(newPower).length > 0) {
      const { rateOverride, buffOverride, actualRate, actualBuff } = newPower
      setAddedValues({ rateOverride, buffOverride, actualRate, actualBuff })
    }
  }, [newPower])

  const _onSaveValues = (values) => {
    onSave({ ...values, ...addedValues })
  }

  let classes = styles.addCase__formContainer
  if (className) classes = `${classes} ${className}`

  return (
    <Form
      form={form}
      name="basic"
      initialValues={{ type: 'STANDARD', ...newPower, remember: true }}
      layout="vertical"
      onFinish={_onSaveValues}
      className={formClassName}
      onValuesChange={(changedValues, allValues) => {
        let id = Object.keys(changedValues)[0]
        let val = changedValues[id]
        let additional = {}
        let newAddedValues = { ...addedValues }
        if (id === 'rate') {
          newAddedValues.rateOverride = val === '' ? false : true
        } else if (id === 'buff') {
          newAddedValues.buffOverride = val === '' ? false : true
        } else if (id === 'bondAmount') {
          if (contract.rate_generator === 'LIABILITY') {
            if (!newAddedValues.rateOverride) {
              let rate = (parseFloat(val) * (parseFloat(contract.rate) / 100))
              if (rate < contract.minimum) rate = contract.minimum
              additional.rate = numeral(rate).format('0.00').replace('.00', '')
              newAddedValues.actualRate = numeral(rate).format('0.00').replace('.00', '')
            }
            if (!newAddedValues.buffOverride) {
              let buff = (parseFloat(val) * (parseFloat(contract.buff) / 100))
              if (buff < contract.minimum) buff = contract.minimum
              additional.buff = numeral(buff).format('0.00').replace('.00', '')
              newAddedValues.actualBuff = numeral(buff).format('0.00').replace('.00', '')
            }
          }
        } else if (id === 'premium') {
          if (contract.rate_generator === 'GROSS') {
            if (!newAddedValues.rateOverride) {
              let rate = (parseFloat(val) * (parseFloat(contract.rate) / 100))
              if (rate < contract.minimum) rate = contract.minimum
              additional.rate = numeral(rate).format('0.00').replace('.00', '')
              newAddedValues.actualRate = numeral(rate).format('0.00').replace('.00', '')
            }
            if (!newAddedValues.buffOverride) {
              let buff = (parseFloat(val) * (parseFloat(contract.buff) / 100))
              if (buff < contract.minimum) buff = contract.minimum
              additional.buff = numeral(buff).format('0.00').replace('.00', '')
              newAddedValues.actualBuff = numeral(buff).format('0.00').replace('.00', '')
            }
          }
        }
        setAddedValues(newAddedValues)
        form.setFieldsValue({ ...allValues, ...additional })
      }}
    >
      <div className={classes}>
        <div className={styles.addCase__small}>
          <span><FontAwesomeIcon icon={faFile} /></span>
          <Form.Item label="Type" name="type" style={{ width: '15%' }} rules={[{ required: true, message: 'Please select the Bond Type' }]}>
            <Select
              id="type"
              options={[
                { value: 'STANDARD', label: 'Standard' },
                { value: 'REWRITE', label: 'Rewrite' },
                { value: 'TRANSFER', label: 'Transfer' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Bond Status" name="bondStatus" style={{ width: '15%' }} rules={[{ required: true, message: 'Please select the Bond Status' }]}>
            <Select
              id="bondStatus"
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'EXECUTED', label: 'Executed' },
                { value: 'VOID', label: 'Void' },
                { value: 'EXONERATED', label: 'Exonerated' }
              ]}
            />
          </Form.Item>
          <Form.Item 
            label="Execution Date"
            name="executionDate">
              <InputMask
                mask="99/99/9999"
                maskChar="_"
                value={newPower?.executionDate || ''}
              >
                {(inputProps) => <Input {...inputProps} id="executionDate" placeholder="MM/DD/YYYY" />}
              </InputMask>
          </Form.Item>
          <Form.Item label="City" name="city">
            <Input id="city" />
          </Form.Item>
          <Form.Item label="State" name="state" style={{ width: '15%' }}>
            <Select
              id="state" 
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toLowerCase() ?? '').toLowerCase().localeCompare((optionB?.label?.toLowerCase() ?? '').toLowerCase())
              }
              options={states ? Object.keys(states).map(key => {
                return { value: key, label: states[key] }
              }) : []}
            />
          </Form.Item>
          <Form.Item label="County" name="county">
            <Input id="county" />
          </Form.Item>
        </div>
        <div className={styles.addCase__small}>
          <span><FontAwesomeIcon icon={faDollarSign} /></span>
          <Form.Item label="Bond Amount" name="bondAmount">
            <Input id="bondAmount" addonBefore="$" />
          </Form.Item>
          <Form.Item label="Gross Premium" name="premium">
            <Input id="premium" addonBefore="$" />
          </Form.Item>
          <Form.Item label="Rate" name="rate">
            <Input id="rate" addonBefore="$" />
          </Form.Item>
          <Form.Item label="Buff" name="buff">
            <Input id="buff" addonBefore="$" />
          </Form.Item>
        </div>
        {(form.getFieldValue('type') === 'REWRITE') && (
          <div className={styles.addCase__small}>
            <span><FontAwesomeIcon icon={faFile} /></span>
            <Form.Item label="Rewrite Status" name="rewriteStatus">
              <Input id="rewriteStatus" />
            </Form.Item>
            <Form.Item label="Original Power Number" name="originalPowerNumber">
              <Input id="originalPowerNumber" />
            </Form.Item>
          </div>
        )}
        <div className={styles.addCase__small}>
          <span><FontAwesomeIcon icon={faUser} /></span>
          <Form.Item label="Defendant First" name="defendantFirst">
            <Input id="defendantFirst" />
          </Form.Item>
          <Form.Item label="Defendant Middle" name="defendantMiddle">
            <Input id="defendantMiddle" />
          </Form.Item>
          <Form.Item label="Defendant Last" name="defendantLast">
            <Input id="defendantLast" />
          </Form.Item>
          <Form.Item label="Date of Birth" name="dob">
            <InputMask
              mask="99/99/9999"
              maskChar="_"
              value={newPower?.dob || ''}
            >
              {(inputProps) => <Input {...inputProps} id="dob" placeholder="MM/DD/YYYY" />}
            </InputMask>
          </Form.Item>
        </div>

        <div className={styles.addCase__small}>
          <span><FontAwesomeIcon icon={faUser} /></span>
          <Form.Item label="Case Number" name="caseNumber">
            <Input id="caseNumber" addonBefore="#" />
          </Form.Item>
          <Form.Item label="Court Name" name="courtName">
            <Input id="courtName" />
          </Form.Item>
          <Form.Item label="Charge" name="charge">
            <Input id="charge" />
          </Form.Item>
        </div>

        <div className={styles.addCase__small}>
          <span><FontAwesomeIcon icon={faUserTieHair} /></span>
          <Form.Item label="Writing Agent First" name="writingFirst">
            <Input id="writingFirst" />
          </Form.Item>
          <Form.Item label="Writing Agent Last" name="writingLast">
            <Input id="writingLast" />
          </Form.Item>
        </div>
      </div>
      <div className={styles.actions__container}>
        <div className={styles.actions}>
          <ButtonPrimary small type="button" color="grey" icon={faBan} onClick={onCancel}>Cancel</ButtonPrimary>
          <ButtonPrimary small color="navy" icon={faCheckCircle}>Save</ButtonPrimary>
        </div>
      </div>
    </Form>
  )
}

export default PowerForm
